import type { MerchantInboxItem, MerchantKpis, MerchantReturnDetail, MerchantReturnItem, MerchantRules, MerchantSettings, TimelineEvent } from "../../domain/merchant";
import { httpJson } from "../http/client";
import { mockInbox, mockKpis, mockRules, mockSettings } from "./mocks";

const USE_MOCKS = (process.env.REACT_APP_USE_MOCKS || "false") === "true";

// --- Response transformers (backend snake_case → frontend camelCase) ---

function transformKpis(raw: any): MerchantKpis {
  return {
    awaitingAction: raw.awaiting_action ?? 0,
    transitVolume: raw.transit_volume ?? 0,
    retentionRate: raw.retention_rate ?? 0,
    automationPct: raw.automation_pct ?? 0,
    financialGuard: raw.financial_guard
      ? {
          totalSavingsSar: raw.financial_guard.total_savings_sar ?? 0,
          deltaPct: raw.financial_guard.delta_pct ?? 0,
          cashRefundedSar: raw.financial_guard.cash_refunded_sar ?? 0,
          creditIssuedSar: raw.financial_guard.credit_issued_sar ?? 0,
        }
      : undefined,
    avgResolutionHours: raw.avg_resolution_hours,
    totalReturns: raw.total_returns,
    totalResolved: raw.total_resolved,
  };
}

function transformInboxItem(raw: any): MerchantInboxItem {
  return {
    returnRequestId: raw.return_request_id ?? raw.ROWID ?? "",
    rma: raw.return_number ?? "",
    orderRef: raw.order_number ?? "",
    orderIdExternal: raw.order_id_external ?? null,
    customerEmail: raw.customer_contact_masked ?? "",
    resolution: (raw.requested_resolution ?? "").toUpperCase(),
    status: (raw.status ?? "").toUpperCase(),
    statusReason: raw.status_reason ?? null,
    requestedAt: raw.requested_at ?? "",
    resolvedAt: raw.resolved_at ?? null,
    totalItemsCount: Number(raw.total_items_count ?? 0),
    totalRequestValue: raw.total_request_value != null ? Number(raw.total_request_value) : null,
    isWarranty: Boolean(raw.is_warranty),
  };
}

function transformRules(raw: any): MerchantRules {
  return {
    returnWindowDays: raw.return_window_days ?? 30,
    autoApprovalThresholdSar: raw.auto_approval_threshold_sar ?? 150,
    acceptStoreCredit: raw.accept_store_credit ?? true,
    allowExchanges: raw.allow_exchanges ?? true,
    autoApproveLowValue: raw.auto_approve_low_value ?? true,
    categoryOverrides: (raw.category_overrides ?? []).map((co: any) => ({
      category: co.category_name ?? "",
      mode: co.rule ?? "STANDARD_WINDOW",
      label: co.category_name ?? "",
      dayLimit: co.day_limit,
    })),
  };
}

function transformReturnItem(raw: any): MerchantReturnItem {
  return {
    returnItemId: raw.return_item_id ?? raw.ROWID ?? "",
    sku: raw.sku ?? "",
    productName: raw.product_name ?? null,
    variantName: raw.variant_name ?? null,
    quantity: Number(raw.quantity ?? 1),
    unitPrice: raw.unit_price != null ? Number(raw.unit_price) : null,
    reasonCode: raw.reason_code ?? "",
    reasonNote: raw.reason_note ?? null,
    decision: (raw.decision ?? "PENDING").toUpperCase(),
    decisionReason: raw.decision_reason ?? null,
  };
}

function transformReturnDetail(raw: any): MerchantReturnDetail {
  return {
    returnRequestId: raw.return_request_id ?? raw.ROWID ?? "",
    returnNumber: raw.return_number ?? "",
    orderNumber: raw.order_number ?? "",
    orderIdExternal: raw.order_id_external ?? null,
    requestedResolution: (raw.requested_resolution ?? "").toUpperCase(),
    status: (raw.status ?? "").toUpperCase(),
    statusReason: raw.status_reason ?? null,
    requestedAt: raw.requested_at ?? "",
    approvedAt: raw.approved_at ?? null,
    receivedAt: raw.received_at ?? null,
    resolvedAt: raw.resolved_at ?? null,
    notesCustomer: raw.notes_customer ?? null,
    notesInternal: raw.notes_internal ?? null,
    totalItemsCount: Number(raw.total_items_count ?? 0),
    totalRequestValue: raw.total_request_value != null ? Number(raw.total_request_value) : null,
    isWarranty: Boolean(raw.is_warranty),
    customerContactMasked: raw.customer_contact_masked ?? null,
    items: (raw.items ?? []).map(transformReturnItem),
  };
}

function transformTimelineEvent(raw: any): TimelineEvent {
  return {
    action: raw.action ?? "",
    actorType: raw.actor_type ?? "",
    actorId: raw.actor_id ?? "",
    details: raw.details ?? null,
    createdAt: raw.created_at ?? "",
  };
}

function transformSettings(raw: any): MerchantSettings {
  return {
    branding: {
      primaryColor: raw.branding?.primary_color ?? "#4f46e5",
      logoUrl: raw.branding?.logo_url,
    },
    portal: {
      supportEmail: raw.portal?.support_email,
      policyUrl: raw.portal?.policy_url,
    },
  };
}

// --- Reverse transformers (frontend camelCase → backend snake_case) ---

function rulesToBackend(rules: MerchantRules): any {
  return {
    return_window_days: rules.returnWindowDays,
    auto_approval_threshold_sar: rules.autoApprovalThresholdSar,
    accept_store_credit: rules.acceptStoreCredit,
    allow_exchanges: rules.allowExchanges,
    auto_approve_low_value: rules.autoApproveLowValue,
    category_overrides: rules.categoryOverrides.map((co) => ({
      category_name: co.category,
      rule: co.mode,
      day_limit: co.dayLimit,
    })),
  };
}

function settingsToBackend(settings: MerchantSettings): any {
  return {
    branding: {
      primary_color: settings.branding.primaryColor,
      logo_url: settings.branding.logoUrl,
    },
    portal: {
      support_email: settings.portal.supportEmail,
      policy_url: settings.portal.policyUrl,
    },
  };
}

// --- API functions (all take tenantSlug) ---

export async function getMerchantKpis(tenantSlug: string): Promise<MerchantKpis> {
  if (USE_MOCKS) return mockKpis;
  const resp = await httpJson<{ ok: boolean; kpis: any }>(`/merchant/${tenantSlug}/kpis`);
  return transformKpis(resp.kpis);
}

export async function getMerchantInbox(tenantSlug: string): Promise<MerchantInboxItem[]> {
  if (USE_MOCKS) return mockInbox;
  const resp = await httpJson<{ ok: boolean; items: any[] }>(`/merchant/${tenantSlug}/returns`);
  return (resp.items || []).map(transformInboxItem);
}

export async function getMerchantReturnDetail(tenantSlug: string, returnNumber: string): Promise<MerchantReturnDetail> {
  const resp = await httpJson<{ ok: boolean; return: any }>(`/merchant/${tenantSlug}/returns/${returnNumber}`);
  return transformReturnDetail(resp.return);
}

export async function approveReturn(tenantSlug: string, returnNumber: string, args?: any) {
  return httpJson<{ ok: boolean }>(`/merchant/${tenantSlug}/returns/${returnNumber}/approve`, {
    method: "POST",
    body: JSON.stringify(args ?? {}),
  });
}

export async function rejectReturn(tenantSlug: string, returnNumber: string, args?: { status_reason?: string; notes_internal?: string }) {
  return httpJson<{ ok: boolean }>(`/merchant/${tenantSlug}/returns/${returnNumber}/reject`, {
    method: "POST",
    body: JSON.stringify(args ?? {}),
  });
}

export async function markReceived(tenantSlug: string, returnNumber: string, args?: any) {
  return httpJson<{ ok: boolean }>(`/merchant/${tenantSlug}/returns/${returnNumber}/received`, {
    method: "POST",
    body: JSON.stringify(args ?? {}),
  });
}

export async function resolveReturn(tenantSlug: string, returnNumber: string, args: any) {
  return httpJson<{ ok: boolean }>(`/merchant/${tenantSlug}/returns/${returnNumber}/resolve`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function getReturnTimeline(tenantSlug: string, returnNumber: string): Promise<TimelineEvent[]> {
  const resp = await httpJson<{ ok: boolean; timeline: any[] }>(`/merchant/${tenantSlug}/returns/${returnNumber}/timeline`);
  return (resp.timeline || []).map(transformTimelineEvent);
}

export async function getMerchantRules(tenantSlug: string): Promise<MerchantRules> {
  if (USE_MOCKS) return mockRules;
  const resp = await httpJson<{ ok: boolean; rules: any }>(`/merchant/${tenantSlug}/rules`);
  return transformRules(resp.rules);
}

export async function saveMerchantRules(tenantSlug: string, next: MerchantRules): Promise<{ ok: true }> {
  if (USE_MOCKS) return { ok: true };
  return httpJson<{ ok: true }>(`/merchant/${tenantSlug}/rules`, {
    method: "PUT",
    body: JSON.stringify(rulesToBackend(next)),
  });
}

export async function getMerchantSettings(tenantSlug: string): Promise<MerchantSettings> {
  if (USE_MOCKS) return mockSettings;
  const resp = await httpJson<{ ok: boolean; settings: any }>(`/merchant/${tenantSlug}/settings`);
  return transformSettings(resp.settings);
}

export async function saveMerchantSettings(tenantSlug: string, next: MerchantSettings): Promise<{ ok: true }> {
  if (USE_MOCKS) return { ok: true };
  return httpJson<{ ok: true }>(`/merchant/${tenantSlug}/settings`, {
    method: "PUT",
    body: JSON.stringify(settingsToBackend(next)),
  });
}
